import PropTypes from 'prop-types';
import React from 'react';
import { Form } from 'react-final-form';
import { Spinner } from '@unite-us/app-components';
import {
  BaseCard,
  BaseCardBody,
  BaseCardHeader,
  Button,
} from '@unite-us/ui';
import Notifier from 'common/helpers/Notifier';
import { useIsClientsDrawer, useCurrentEmployee } from '../../context/InteractionsContext';
import { computeQuantityValue, computeStartEnd } from '../../utils/computeStartEnd';
import { buildInitialValues } from '../../utils/buildInitialValues';
import { validateStructuredInteractionForm } from '../../utils/validateStructuredInteractionForm';
import { useCreateInteraction } from '../../hooks/useCreateInteraction';
import { useCreateProcedure } from '../../hooks/useCreateProcedure';
import { useUpdateInteraction } from '../../hooks/useUpdateInteraction';
import { useUpdateProcedure } from '../../hooks/useUpdateProcedure';
import { useInteractionConcepts } from '../../hooks/useInteractionConcepts';
import { usePopulate } from '../../hooks/APIHooks';
import { PERIOD_TYPE } from '../../constants';
import { InteractionTypeRow } from './InteractionTypeRow';
import { PeriodOfServiceRow } from './PeriodOfServiceRow';
import { DurationRow } from './DurationRow';
import { NoteField } from './NoteField';

export function StructuredInteractionsForm({
  interaction,
  isEditing,
  onClose,
}) {
  const isClientsDrawer = useIsClientsDrawer();
  const currentEmployee = useCurrentEmployee();
  const {
    typeConcept,
    procedureOptions,
    isLoading,
  } = useInteractionConcepts();

  const { isLoading: isProceduresLoading } = usePopulate(
    'procedures',
    'procedure',
    interaction,
    {
      queryConfig: {
        placeholderData: undefined,
        enabled: Boolean(isEditing && interaction),
      },
    },
  );

  const createInteraction = useCreateInteraction();
  const createProcedure = useCreateProcedure();
  const { updateRecord: updateInteraction } = useUpdateInteraction();
  const { updateRecord: updateProcedure } = useUpdateProcedure();
  const isEditDataLoading = isEditing && isProceduresLoading;
  const onSubmit = async (values) => {
    const { start_time, end_time } = computeStartEnd(values);
    const quantity_value = computeQuantityValue(values);

    try {
      if (isEditing) {
        const procedureId = interaction.procedures?.[0]?.id;

        await updateInteraction(interaction.id, {
          note: values.note || undefined,
          start_time,
          end_time,
          recorded_by: currentEmployee?.id,
        });

        if (procedureId) {
          await updateProcedure(procedureId, {
            code_concept: values.codeConceptId,
            note: values.note || undefined,
            performed_start_time: start_time,
            performed_end_time: end_time,
            quantity_value,
            recorded_by: currentEmployee?.id,
          });
        }

        Notifier.dispatch('success', 'Interaction updated.');
      } else {
        const interactionResponse = await createInteraction({
          note: values.note,
          start_time,
          end_time,
        });
        const interactionId = interactionResponse?.data?.data?.id;

        if (!interactionId) {
          throw new Error('Interaction was created but no id was returned.');
        }

        await createProcedure({
          interactionId,
          codeConceptId: values.codeConceptId,
          note: values.note,
          performed_start_time: start_time,
          performed_end_time: end_time,
          quantity_value,
        });

        Notifier.dispatch('success', 'Interaction saved.');
      }

      onClose();
    } catch (error) {
      Notifier.handleErrors(error);
    }
  };

  const initialValues = isEditing ?
    buildInitialValues(interaction) :
    { periodType: PERIOD_TYPE.SINGLE };

  return (
    <div
      data-testid="structured-interactions-form"
      data-test-element="structured-interactions-form"
    >
      <BaseCard
        className={isClientsDrawer ? 'mb-4' : 'mb-6'}
      >
        <BaseCardHeader title={isEditing ? 'Edit Interaction' : 'New Interaction'} />
        <BaseCardBody withPadding>
          {isEditDataLoading ? (
            <Spinner scale={0.45} />
          ) : (
            <Form
              key={isEditing ? interaction.id : 'new-interaction'}
              initialValues={initialValues}
              onSubmit={onSubmit}
              validate={validateStructuredInteractionForm}
              render={({
                handleSubmit,
                form,
                values,
                submitting,
                submitFailed,
              }) => (
                <form onSubmit={handleSubmit}>
                  <div className="grid w-full grid-cols-1 gap-y-6">
                    <div className="col-span-1 min-w-0 w-full">
                      <InteractionTypeRow
                        concept={typeConcept}
                        conceptLoading={isLoading}
                        procedureOptions={procedureOptions}
                        procedureLoading={isLoading}
                        submitFailed={submitFailed}
                      />
                    </div>
                    <div className="col-span-1 min-w-0 w-full">
                      <PeriodOfServiceRow periodType={values.periodType} />
                    </div>
                    <div className="col-span-1 min-w-0 w-full">
                      <DurationRow />
                    </div>
                    <div className="col-span-1 min-w-0 w-full">
                      <NoteField />
                    </div>
                  </div>
                  <div className="mt-6 grid auto-cols-max grid-flow-col justify-end gap-3">
                    <Button
                      className="font-regular-font"
                      data-testid="structured-interaction-cancel"
                      id="structured-interactions-cancel"
                      label="Cancel"
                      secondary
                      onClick={() => {
                        form.reset();
                        onClose();
                      }}
                      type="button"
                    />
                    <Button
                      className="font-regular-font"
                      data-testid="structured-interaction-save"
                      disabled={submitting || isLoading}
                      id="structured-interactions-save"
                      label="Save Interaction"
                      primary
                      type="submit"
                    />
                  </div>
                </form>
              )}
            />
          )}
        </BaseCardBody>
      </BaseCard>
    </div>
  );
}

StructuredInteractionsForm.propTypes = {
  interaction: PropTypes.shape({
    id: PropTypes.string,
    note: PropTypes.string,
    start_time: PropTypes.string,
    end_time: PropTypes.string,
    procedures: PropTypes.arrayOf(PropTypes.object),
  }),
  isEditing: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};

StructuredInteractionsForm.defaultProps = {
  interaction: null,
  isEditing: false,
};
